import { FC, useCallback } from 'react';
import { Grid } from '@mui/material';
import { handleDatePickerMonthChange } from '../../utils/componentUtils';
import CustomDatePicker from '../UI/CustomDatePicker';
import WorkingUnitLocationSelect from '../Selects/WorkingUnitLocationSelect';
import { WorkingUnitOption } from '../Selects/WorkingUnitSelect';
import { Location } from '../../types/Location.interface';
import { appTimezone } from '../../config';
import type { Dayjs } from '@utils/dayjs';
import dayjs from '@utils/dayjs';

interface BiddingCalculatedFiltersProps {
    locationOptions: Location[];
    selectedLocation: string | number;
    onSelectedLocationChange: (selectedLocation: number) => void;
    selectedWorkingUnitLocation: number;
    onSelectedWorkingUnitLocationChange: (
        workingUnitLocationId: number,
        workingUnitLocation?: WorkingUnitOption
    ) => void;
    selectedDate: Dayjs;
    onSelectedDateChange: (date: Dayjs) => void;
    workingUnitHasMatchingOfferedCluster?: boolean;
    releasedOnly?: boolean;
}

const BiddingCalculatedFilters: FC<BiddingCalculatedFiltersProps> = ({
    locationOptions,
    selectedLocation,
    onSelectedLocationChange,
    selectedWorkingUnitLocation,
    onSelectedWorkingUnitLocationChange,
    selectedDate,
    onSelectedDateChange,
    workingUnitHasMatchingOfferedCluster,
    releasedOnly,
}) => {
    // Handle working unit change with date auto-adjustment
    const handleWorkingUnitLocationChange = useCallback(
        (workingUnitLocationId: number, workingUnitLocation?: WorkingUnitOption) => {
            onSelectedWorkingUnitLocationChange(workingUnitLocationId, workingUnitLocation);

            // Auto-adjust date if working unit's latest data is earlier than selected date
            if (workingUnitLocation?.latestDataMonth) {
                // Parse YYYY-MM string directly in app timezone
                const latestDataDate = dayjs.tz(workingUnitLocation.latestDataMonth, 'YYYY-MM', appTimezone);
                if (latestDataDate.isValid() && latestDataDate.isBefore(selectedDate, 'month')) {
                    onSelectedDateChange(latestDataDate);
                }
            }
        },
        [onSelectedWorkingUnitLocationChange, selectedDate, onSelectedDateChange]
    );

    return (
        <>
            <WorkingUnitLocationSelect
                locationOptions={locationOptions}
                selectedLocation={selectedLocation ? Number(selectedLocation) : 0}
                onSelectedLocationChange={onSelectedLocationChange}
                selectedWorkingUnitLocation={selectedWorkingUnitLocation}
                onSelectedWorkingUnitLocationChange={handleWorkingUnitLocationChange}
                workingUnitHasMatchingOfferedCluster={workingUnitHasMatchingOfferedCluster}
                includeLatestDataMonth={workingUnitHasMatchingOfferedCluster}
                includeCorrectedTotalsInfo={workingUnitHasMatchingOfferedCluster}
                releasedOnly={releasedOnly}
            />
            <Grid item xs={3} md={2} xl={1}>
                <CustomDatePicker
                    date={selectedDate}
                    views={['year', 'month']}
                    openTo="month"
                    onChange={(newValue: Dayjs) =>
                        handleDatePickerMonthChange(selectedDate, newValue, onSelectedDateChange)
                    }
                />
            </Grid>
        </>
    );
};

export default BiddingCalculatedFilters;
